import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { type BucketCount } from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

const { coachingAnalytics } = appStrings.modules;

const STAGE_LABELS: Readonly<Record<string, string>> = {
  high_school: coachingAnalytics.breakdown.highSchool,
  post_secondary: coachingAnalytics.breakdown.postSecondary,
  none: coachingAnalytics.breakdown.notEnrolled,
  unknown: coachingAnalytics.breakdown.unknown,
};

const PROGRAM_LABELS: Readonly<Record<string, string>> = coachingAnalytics.filters.programNames;

const { targetCareerTracks } = coachingAnalytics.blueprint;

const CAREER_TIER_LABELS: Readonly<Record<string, string>> = {
  top_tier: targetCareerTracks.topTier,
  upper_tier: targetCareerTracks.upperTier,
  middle_tier: targetCareerTracks.middleTier,
  trades_technical: targetCareerTracks.tradesTechnical,
  semi_skilled: targetCareerTracks.semiSkilled,
};

const UNLOCK_STATUS_LABELS: Readonly<Record<string, string>> = {
  on_track: coachingAnalytics.onTrack.legend.onTrack,
  developing: coachingAnalytics.onTrack.legend.developing,
  not_yet: coachingAnalytics.onTrack.legend.notYet,
};

const ALIGNMENT_LABELS: Readonly<Record<string, string>> = {
  ahead: coachingAnalytics.onTrack.aspiration.legend.ahead,
  aligned: coachingAnalytics.onTrack.aspiration.legend.aligned,
  behind: coachingAnalytics.onTrack.aspiration.legend.behind,
};

const { completion } = coachingAnalytics.lifeReadiness;

const COMPLETION_STATUS_LABELS: Readonly<Record<string, string>> = {
  completed: completion.completed,
  in_progress: completion.inProgress,
  not_started: completion.notStarted,
};

// Reuses the questionnaire's own rung names.
const RUNG_LABELS = appStrings.modules.lifeReadiness.questionTooltips.consolidated;

const READINESS_BAND_LABELS: Readonly<Record<string, string>> = {
  not_yet: RUNG_LABELS.notYet.title,
  early: RUNG_LABELS.early.title,
  strong: RUNG_LABELS.strong.title,
  advanced: RUNG_LABELS.advanced.title,
};

const TOOL_LABELS: Readonly<Record<string, string>> = coachingAnalytics.toolUsage.tools;

const MEASURE_LABELS: Readonly<Record<string, string>> = {
  gpa: coachingAnalytics.onTrack.academicProfile.gpa,
  sat: coachingAnalytics.onTrack.academicProfile.sat,
  act: coachingAnalytics.onTrack.academicProfile.act,
};

const labelFrom =
  (labels: Readonly<Record<string, string>>) =>
  (id: string): string =>
    labels[id] ?? humanize(id);

export const percentOf = (count: number, total: number): number =>
  total > 0 ? Math.round((count / total) * 100) : 0;

/**
 * Percentages for counts that partition a whole, rounded so they still sum to
 * 100 — the surplus from rounding each share separately would otherwise show
 * as a gap in a pie.
 */
export const percentShares = (counts: readonly number[], total: number): number[] => {
  if (total <= 0) return counts.map(() => 0);

  const exact = counts.map((count) => (count / total) * 100);
  const shares = exact.map((value) => Math.floor(value));
  const surplus = 100 - shares.reduce((sum, share) => sum + share, 0);

  exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder)
    .slice(0, Math.max(0, surplus))
    .forEach(({ index }) => {
      shares[index] += 1;
    });

  return shares;
};

export const bucketCount = (buckets: readonly BucketCount[], id: string): number =>
  buckets.find((bucket) => bucket.id === id)?.count ?? 0;

export const humanize = (value: string | null): string =>
  (value ?? '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export const stageLabel = labelFrom(STAGE_LABELS);
export const programLabel = labelFrom(PROGRAM_LABELS);
export const careerTierLabel = labelFrom(CAREER_TIER_LABELS);
export const unlockStatusLabel = labelFrom(UNLOCK_STATUS_LABELS);
export const alignmentLabel = labelFrom(ALIGNMENT_LABELS);
export const completionStatusLabel = labelFrom(COMPLETION_STATUS_LABELS);
export const readinessBandLabel = labelFrom(READINESS_BAND_LABELS);
export const toolLabel = labelFrom(TOOL_LABELS);
export const measureLabel = labelFrom(MEASURE_LABELS);
